import { Grid, Typography, Button, CircularProgress, RadioGroup, FormControlLabel, Radio } from "@material-ui/core"
import { useState } from "react";

import SimpleModal from "../../shared/SimpleModal";
import loginService from "../../../domain/services/login.service";

import { trackPromise } from 'react-promise-tracker';
import memberService from "../../../domain/services/solicitation/member.service";

const RequestIaRatingDialog = ({ request = {}, setAlert, isOpen, onClose, onFinish }) => {
    
    const fadeInStyle = `
        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: translateY(-10px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
    `;

    const styles = {
        questionContainer: (borderColor, bgColor, animate = false) => ({
            className: "mb-10 p-4 border-l-4 rounded-r-lg",
            style: { 
                borderColor, 
                backgroundColor: bgColor,
                animation: animate ? 'fadeIn 0.3s ease-in-out' : 'none'
            }
        }),
        questionTitle: (color) => ({
            variant: "h5",
            component: "legend",
            style: {
                fontWeight: 600,
                color,
                fontSize: '1.35rem',
                marginBottom: '20px',
                display: 'block'
            }
        }),
        radioGroup: {
            style: {
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
            }
        },
        radioControl: (color) => ({
            style: {
                color,
                padding: '4px 6px 4px 4px'
            }
        }),
        radioLabel: {
            style: {
                fontSize: '1.05rem',
                fontWeight: 400,
                color: '#444',
                marginLeft: '4px',
                lineHeight: 1.5
            }
        },
        radioItem: {
            style: {
                margin: 0,
                padding: '6px 8px',
                borderRadius: 8,
                transition: 'background-color 0.2s ease',
                alignItems: 'center'
            },
            className: "hover:bg-gray-100"
        }
    };

    const [responses, setResponses] = useState({
        neededRedo: false,
        faciliterDetails: ''
    });
    const [loading, setLoading] = useState(false);

    const customerUserId = loginService.getUserAuthentication().id;

    const mainRequestId = request.mainId || request.id;

    const handleChange = (field, value) =>
        setResponses({ ...responses, [field]: value });

    const handleSubmit = async () => {
        setLoading(true);
        trackPromise(
            saveRating(responses)
                .then(res => { if (res) { onFinish(); setLoading(false) } })
                .catch((err) => setAlert(err.response?.data?.message))
        );
    };
 
    const saveRating = (responseData) =>
        memberService.saveServiceIaRating({
            requestId: mainRequestId,
            userId: customerUserId,
            neededRedo: responseData.neededRedo,
            faciliterDetails: responseData.faciliterDetails
        });

    const isFormValid = () => {
        if (responses.neededRedo === null) return false;
        if (responses.neededRedo !== null && !responses.faciliterDetails.trim()) return false;
        return true;
    };

    const questionConfigs = {
        primary: {
            title: "Você precisou refazer mais de 80% da petição?",
            fieldName: "neededRedo",
            borderColor: "#3b82f6",
            bgColor: "#f8faff", 
            radioColor: "#1976d2",
            options: [
                { value: true, label: "Sim, precisei refazer mais de 80% da petição" },
                { value: false, label: "Não" }
            ]
        }
    };

    const RadioQuestion = ({ 
        title, 
        value, 
        onChange, 
        options, 
        borderColor, 
        bgColor, 
        radioColor,
        fieldName,
        animate = false
    }) => {
        const containerProps = styles.questionContainer(borderColor, bgColor, animate);
        const titleProps = styles.questionTitle(radioColor);
        
        return (
            <div className={containerProps.className} style={containerProps.style}>
                <Typography {...titleProps}>
                    {title}
                </Typography>
                <RadioGroup
                    value={value}
                    onChange={(e) => onChange(fieldName, e.target.value === 'true')}
                    style={styles.radioGroup.style}
                >
                    {options.map((option, index) => (
                        <FormControlLabel
                            key={index}
                            value={option.value}
                            control={<Radio style={styles.radioControl(radioColor).style} />}
                            label={
                                <Typography variant="body1" style={styles.radioLabel.style}>
                                    {option.label}
                                </Typography>
                            }
                            style={styles.radioItem.style}
                            className={styles.radioItem.className}
                        />
                    ))}
                </RadioGroup>
            </div>
        );
    };

    return (
        <>
            <style>{fadeInStyle}</style>
            <SimpleModal
                open={isOpen}
                onClose={onClose}
                maxWidth="md"
                bodyStyle={{ padding: 48, borderRadius: 12 }}
            >
            <div className="text-center mb-8">
                <Typography 
                    variant="h4" 
                    component="h2"
                    style={{ 
                        fontWeight: 500, 
                        color: '#1a1a1a',
                        lineHeight: 1.3,
                        marginBottom: 20,
                        fontSize: '1.75rem'
                    }}
                >
                    Antes de concluir, nos conte como foi sua experiência com a revisão do documento
                </Typography>
                <div 
                    style={{ 
                        width: 60, 
                        height: 4, 
                        backgroundColor: '#1976d2', 
                        margin: '0 auto',
                        borderRadius: 2
                    }} 
                />
            </div>

            <Grid className="text-base">
                
                <RadioQuestion
                    {...questionConfigs.primary}
                    value={responses[questionConfigs.primary.fieldName]}
                    onChange={handleChange}
                />

                {responses.neededRedo !== null && (
                    <div 
                        className="mb-10 p-4 border-l-4 rounded-r-lg"
                        style={{ 
                            borderColor: responses.neededRedo === true ? '#f97316' : '#22c55e',
                            backgroundColor: responses.neededRedo === true ? '#fff7ed' : '#f0fdf4',
                            animation: 'fadeIn 0.3s ease-in-out'
                        }}
                    >
                        <Typography 
                            variant="h5"
                            component="legend"
                            style={{ 
                                fontWeight: 600,
                                color: responses.neededRedo === true ? '#ea580c' : '#059669',
                                fontSize: '1.35rem',
                                marginBottom: '20px',
                                display: 'block'
                            }}
                        >
                            {responses.neededRedo === true 
                                ? 'Informe quais seções foram refeitas e detalhe os motivos. Essas informações são essenciais para que possamos avaliar corretamente o nível de refazimento e considerar o pagamento adicional.'
                                : 'Conte-nos sobre sua experiência com o documento gerado. Houve algum ajuste necessário?'
                            }
                        </Typography>
                        <textarea 
                            className={`w-full border-2 rounded-md focus:outline-none transition-colors duration-200 ${
                                responses.neededRedo === true 
                                    ? 'focus:border-orange-500 border-orange-300' 
                                    : 'focus:border-green-500 border-green-300'
                            }`}
                            style={{ 
                                maxHeight: '300px',
                                fontSize: '1.05rem',
                                fontFamily: 'inherit',
                                resize: 'none',
                                backgroundColor: '#ffffff',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                padding: '12px',
                                lineHeight: '1.5',
                                height: 'auto',
                                overflow: 'auto'
                            }}
                            value={responses.faciliterDetails}
                            onChange={(e) => {
                                handleChange('faciliterDetails', e.target.value);
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.min(600, Math.max(100, e.target.scrollHeight)) + 'px';
                            }}
                            onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.min(600, Math.max(100, e.target.scrollHeight)) + 'px';
                            }}
                            placeholder={responses.neededRedo === true 
                                ? "Descreva detalhadamente quais seções foram refeitas e os motivos específicos para cada uma..."
                                : "Compartilhe sua experiência: o que funcionou bem, o que poderia ser melhorado..."
                            }
                        />
                    </div>
                )}

                <div 
                    className="p-6 bg-gray-50 rounded-lg text-center"
                    style={{ backgroundColor: '#f9fafb', marginTop: 40 }}
                >
                    <Typography 
                        variant="body1" 
                        style={{ 
                            lineHeight: 1.6, 
                            marginBottom: 24,
                            color: '#6b7280',
                            fontSize: '1.1rem',
                            fontWeight: 400
                        }}
                    >
                        Obrigado pela sua colaboração! Seu feedback é essencial para aprimorarmos continuamente nossos serviços. 🚀
                    </Typography>

                    <Button
                        color="primary"
                        variant="contained"
                        size="large"
                        disabled={!isFormValid()}
                        onClick={handleSubmit}
                        style={{
                            padding: '16px 32px',
                            borderRadius: 8,
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                            transition: 'all 0.2s ease-in-out'
                        }}
                        className="hover:shadow-lg"
                    >
                        <span>
                            Enviar avaliação e concluir serviço
                        </span>
                        {loading && (
                            <CircularProgress 
                                size={20} 
                                style={{ 
                                    marginLeft: 12,
                                    color: 'white'
                                }} 
                            />
                        )}
                    </Button>
                </div>
            </Grid>

            </SimpleModal>
        </>
    )
}

export default RequestIaRatingDialog;
